import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, AlertCircle, Mail } from "lucide-react";
import { LogoMark } from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling");
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState("error");
            return;
        }
        let cancelled = false;
        const poll = async () => {
            attempts.current += 1;
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    if (!cancelled) setState("paid");
                    return;
                }
            } catch (e) {
                if (e.response?.status === 404) {
                    if (!cancelled) setState("error");
                    return;
                }
            }
            if (!cancelled && attempts.current < 20) setTimeout(poll, 2500);
            else if (!cancelled) setState("timeout");
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <div className="grain relative flex min-h-screen items-center justify-center bg-[linear-gradient(135deg,#0C2A52,#071731)] px-4 py-16">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-lg rounded-2xl bg-white p-8 text-center shadow-2xl sm:p-10"
                data-testid="order-confirmation-card"
            >
                <div className="mx-auto w-fit">
                    <LogoMark size={48} />
                </div>

                {state === "polling" && (
                    <>
                        <Loader2 className="mx-auto mt-8 h-10 w-10 animate-spin text-gold" />
                        <h1 className="mt-5 font-display text-2xl font-extrabold text-navy-950">
                            Stiamo confermando il pagamento…
                        </h1>
                        <p className="mt-3 text-sm text-slate-500">
                            Attendi qualche secondo: non chiudere questa pagina.
                        </p>
                    </>
                )}

                {state === "paid" && (
                    <>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }}
                        >
                            <CheckCircle2 className="mx-auto mt-8 h-14 w-14 text-emerald-500" />
                        </motion.div>
                        <h1 className="mt-5 font-display text-2xl font-extrabold text-navy-950 sm:text-3xl">
                            Ordine confermato
                        </h1>
                        <p className="mt-3 text-sm leading-relaxed text-slate-500">
                            Grazie per l'acquisto di <strong className="text-navy-950">Difesa AMM 2026 — 3 Simulazioni
                            Complete</strong>. Il tuo PDF è pronto.
                        </p>
                        <a
                            data-testid="confirmation-download-btn"
                            href={`${API}/download/${sessionId}`}
                            className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-gold px-7 py-4 font-bold text-navy-950 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-gold-hover hover:shadow-[0_12px_36px_rgba(240,180,41,0.4)]"
                        >
                            <Download className="h-5 w-5" />
                            Scarica il PDF
                        </a>
                        <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-400">
                            <Mail className="h-3.5 w-3.5" />
                            Ti abbiamo inviato anche un'email con il link per scaricarlo.
                        </p>
                    </>
                )}

                {(state === "error" || state === "timeout") && (
                    <>
                        <AlertCircle className="mx-auto mt-8 h-12 w-12 text-amber-500" />
                        <h1 className="mt-5 font-display text-2xl font-extrabold text-navy-950">
                            {state === "timeout" ? "Conferma in corso" : "Qualcosa non va"}
                        </h1>
                        <p className="mt-3 text-sm leading-relaxed text-slate-500">
                            {state === "timeout"
                                ? "Il pagamento richiede ancora qualche istante: se hai completato l'acquisto, riceverai a breve un'email con il link per scaricare il PDF."
                                : "Non siamo riusciti a verificare questo ordine. Se hai completato il pagamento, contattaci dalla pagina Contatti indicando la tua email."}
                        </p>
                    </>
                )}

                <Link
                    to="/"
                    className="mt-8 inline-block text-xs font-semibold text-slate-400 transition-colors hover:text-gold"
                >
                    ← Torna alla pagina principale
                </Link>
            </motion.div>
        </div>
    );
}
